/**
 * F1 (paridad Ledgeria) — LIBRO MAYOR.
 *
 * Extracto de una cuenta con saldo anterior y saldo acumulado movimiento a
 * movimiento. Con las subcuentas por tercero de F0, escribir "43000006" da el
 * estado de cuenta de ESE cliente, y "430" el de todos los clientes.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, BookOpenCheck, ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../lib/api'
import { currentLocaleTag } from '../lib/formatting'
import { formatCents } from '../lib/money'
import type { LedgerAccountOption, LedgerResponse } from '@toga/shared'

const inputCls = 'rounded-md border px-2 py-1.5 text-sm'
const inputStyle = { background: 'var(--bg-input)', borderColor: 'var(--border)' } as const

/** Saldo en formato contable: deudor en positivo, acreedor entre paréntesis. */
function SaldoCell({ cents }: { cents: number }) {
  const isCredit = cents < 0
  return (
    <span
      className={`font-mono tabular-nums ${
        isCredit ? 'text-amber-700 dark:text-amber-300' : 't-primary'
      }`}
    >
      {isCredit ? `(${formatCents(Math.abs(cents))})` : formatCents(cents)}
    </span>
  )
}

export default function AccountingLedger() {
  const { t } = useTranslation(['accounting', 'common'])
  const currentYear = new Date().getFullYear()

  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<LedgerAccountOption[]>([])
  const [accountCode, setAccountCode] = useState('')
  const [year, setYear] = useState<string>(String(currentYear))
  const [includeChildren, setIncludeChildren] = useState(true)
  const [page, setPage] = useState(1)

  const [data, setData] = useState<LedgerResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Buscador de cuentas (con movimiento primero).
  useEffect(() => {
    let cancelled = false
    const handle = setTimeout(() => {
      const params = new URLSearchParams()
      if (query.trim()) params.set('q', query.trim())
      api
        .get<{ items: LedgerAccountOption[] }>(`/accounting/ledger/accounts?${params}`)
        .then((res) => {
          if (!cancelled) setOptions(res.items)
        })
        .catch(() => {
          if (!cancelled) setOptions([])
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [query])

  const load = useCallback(async () => {
    if (!accountCode) {
      setData(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({
        accountCode,
        includeChildren: String(includeChildren),
        page: String(page),
      })
      if (year) params.set('year', year)
      setData(await api.get<LedgerResponse>(`/accounting/ledger?${params}`))
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('ledger.loadError')))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [accountCode, includeChildren, page, year, t])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    setPage(1)
  }, [accountCode, year, includeChildren])

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1),
    [data]
  )
  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(currentLocaleTag())

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-serif font-semibold t-primary flex items-center gap-2">
          <BookOpenCheck size={22} className="text-toga-700 dark:text-toga-300" />
          {t('ledger.title')}
        </h1>
        <p className="t-muted text-sm mt-1">{t('ledger.subtitle')}</p>
      </header>

      {/* Filtros */}
      <div className="surface p-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 min-w-[18rem] flex-1">
          <span className="text-xs t-muted">{t('ledger.account')}</span>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 t-faint" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('ledger.searchPlaceholder')}
              className={`${inputCls} w-full pl-7`}
              style={inputStyle}
              list="ledger-accounts"
            />
          </div>
          <datalist id="ledger-accounts">
            {options.map((o) => (
              <option key={o.code} value={o.code}>
                {o.name}
              </option>
            ))}
          </datalist>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs t-muted">{t('ledger.selected')}</span>
          <select
            value={accountCode}
            onChange={(e) => setAccountCode(e.target.value)}
            className={`${inputCls} min-w-[16rem]`}
            style={inputStyle}
          >
            <option value="">{t('ledger.pickAccount')}</option>
            {options.map((o) => (
              <option key={o.code} value={o.code}>
                {o.code} · {o.name}
                {o.hasMovements ? '' : ` (${t('ledger.noMovements')})`}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs t-muted">{t('ledger.year')}</span>
          <input
            value={year}
            onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
            className={`${inputCls} w-24`}
            style={inputStyle}
            inputMode="numeric"
          />
        </label>

        <label className="flex items-center gap-2 text-sm t-secondary pb-1.5">
          <input
            type="checkbox"
            checked={includeChildren}
            onChange={(e) => setIncludeChildren(e.target.checked)}
            className="h-4 w-4 accent-toga-600"
          />
          {t('ledger.includeChildren')}
        </label>
      </div>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {!accountCode && !loading && (
        <div className="surface p-8 text-center t-muted text-sm">{t('ledger.pickToStart')}</div>
      )}

      {loading && (
        <div className="surface p-8 text-center t-muted text-sm">
          <Loader2 size={18} className="inline animate-spin mr-2" /> {t('common:loading')}
        </div>
      )}

      {data && !loading && (
        <>
          {/* Cabecera de saldos */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="surface p-3">
              <div className="text-[10px] uppercase tracking-wider t-faint">
                {t('ledger.openingBalance')}
              </div>
              <div className="text-lg mt-1">
                <SaldoCell cents={data.openingBalanceCents} />
              </div>
            </div>
            <div className="surface p-3">
              <div className="text-[10px] uppercase tracking-wider t-faint">{t('ledger.debit')}</div>
              <div className="text-lg mt-1 font-mono tabular-nums t-primary">
                {formatCents(data.totalDebitCents)}
              </div>
            </div>
            <div className="surface p-3">
              <div className="text-[10px] uppercase tracking-wider t-faint">{t('ledger.credit')}</div>
              <div className="text-lg mt-1 font-mono tabular-nums t-primary">
                {formatCents(data.totalCreditCents)}
              </div>
            </div>
            <div className="surface p-3 ring-1 ring-toga-500/20">
              <div className="text-[10px] uppercase tracking-wider text-toga-700 dark:text-toga-300">
                {t('ledger.closingBalance')}
              </div>
              <div className="text-lg mt-1">
                <SaldoCell cents={data.closingBalanceCents} />
              </div>
            </div>
          </div>

          {/* Movimientos */}
          <div className="surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
                  <th className="px-3 py-2 font-medium">{t('ledger.col.date')}</th>
                  <th className="px-3 py-2 font-medium">{t('ledger.col.entry')}</th>
                  <th className="px-3 py-2 font-medium">{t('ledger.col.concept')}</th>
                  {includeChildren && (
                    <th className="px-3 py-2 font-medium">{t('ledger.col.account')}</th>
                  )}
                  <th className="px-3 py-2 font-medium text-right">{t('ledger.col.debit')}</th>
                  <th className="px-3 py-2 font-medium text-right">{t('ledger.col.credit')}</th>
                  <th className="px-3 py-2 font-medium text-right">{t('ledger.col.balance')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-base/50 bg-[var(--bg-input-soft)]">
                  <td className="px-3 py-2 t-muted text-xs" colSpan={includeChildren ? 6 : 5}>
                    {t('ledger.openingRow')}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <SaldoCell cents={data.openingBalanceCents} />
                  </td>
                </tr>
                {data.movements.length === 0 ? (
                  <tr>
                    <td
                      colSpan={includeChildren ? 7 : 6}
                      className="px-3 py-8 text-center t-muted text-sm"
                    >
                      {t('ledger.empty')}
                    </td>
                  </tr>
                ) : (
                  data.movements.map((m) => (
                    <tr key={m.lineId} className="border-b border-base/40 last:border-b-0 hover:bg-hover/40">
                      <td className="px-3 py-2 t-secondary text-xs whitespace-nowrap">
                        {fmtDate(m.entryDate)}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs t-muted whitespace-nowrap">
                        {m.entryYear}/{m.entryNumber}
                      </td>
                      <td className="px-3 py-2 t-primary">
                        {m.description || m.entryDescription}
                      </td>
                      {includeChildren && (
                        <td className="px-3 py-2 font-mono text-xs t-muted whitespace-nowrap">
                          {m.accountCode}
                        </td>
                      )}
                      <td className="px-3 py-2 text-right font-mono tabular-nums t-secondary">
                        {m.debitCents ? formatCents(m.debitCents) : ''}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums t-secondary">
                        {m.creditCents ? formatCents(m.creditCents) : ''}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <SaldoCell cents={m.balanceCents} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="t-muted">
                {t('ledger.pageOf', { page: data.page, pages: totalPages, total: data.total })}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  disabled={data.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-2 py-1 rounded border border-base disabled:opacity-40 hover:bg-hover"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  type="button"
                  disabled={data.page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-2 py-1 rounded border border-base disabled:opacity-40 hover:bg-hover"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
